<template>
  <!-- @click.stop Avoids bubbling up click events -->
  <div
    :class="classes"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <span
      ref="toggle"
      class="tooltip__toggle"
      tabindex="0"
      role="button"
      data-test-id="tooltip-toggle"
      :aria-expanded="opened"
      :aria-controls="$id('tooltip-content')"
      @click="toggle"
      @keyup.self.escape="close"
      @keypress.self.space.prevent="toggle"
      @keypress.self.enter="toggle"
    >
      <slot />
      <transition name="fade">
        <span
          v-show="visible"
          class="tooltip__arrow"
          aria-hidden="true"
        />
      </transition>
    </span>
    <transition name="fade">
      <div
        v-show="visible"
        :id="$id('tooltip-content')"
        class="tooltip__content"
        data-test-id="tooltip-content"
        @keyup.escape="close"
      >
        <slot name="content" />
      </div>
    </transition>
  </div>
</template>

<script>
import classes from '~~/common/vue/mixins/classes';
import { TOOLTIP_HIDE_DELAY } from '~~/common/js/constants/events';

export const sides = [
  'left-top',
  'left-center',
  'left-bottom',
  'right-top',
  'right-center',
  'right-bottom',
  'center-top',
  'center-bottom',
];
export const sizes = ['m', 's'];

export default {
  name: 'ETooltip',
  mixins: [classes],
  props: {
    padding: {
      type: Boolean,
      default: true,
      cssClass: true,
    },
    side: {
      type: String,
      default: 'left-top',
      validator: (value) => sides.includes(value),
      cssClass: true,
    },
    size: {
      type: String,
      default: 'm',
      validator: (value) => sizes.includes(value),
      cssClass: true,
    },
  },
  data() {
    return {
      hovered: false,
      opened: false,
    };
  },
  computed: {
    visible() {
      return this.opened || this.hovered;
    },
  },
  watch: {
    opened(isOpened) {
      if (isOpened) {
        document.addEventListener('scroll', this.scrollListener);
        this.$emit('tooltip-opened');
      } else {
        document.removeEventListener('scroll', this.scrollListener);
        // mobile browsers also fire `mouseenter` when the toggle is tapped
        // so we have to also "unhover" when closing.
        this.hovered = false;
      }
    },
    hovered(isHovered) {
      if (isHovered) {
        this.$emit('tooltip-opened');
      }
    },
  },
  beforeMount() {
    this.scrollListener = () => this.close();
  },
  methods: {
    onMouseEnter() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
      this.hovered = true;
      this.$emit('tooltip-hovered');
    },
    onMouseLeave() {
      if (!this.timeout) {
        this.timeout = setTimeout(() => {
          this.hovered = false;
        }, TOOLTIP_HIDE_DELAY);
      }
    },
    toggle() {
      this.opened = !this.opened;
    },
    close() {
      this.$refs?.toggle.focus();
      this.opened = false;
    },
  },
};
</script>

<style lang="scss" scoped>
$max-width: 280px;
$sides: (
  center-top: (
    left: 50%,
    content-x: -50%,
    content-y: -100%,
    content-offset-x: -50%,
    content-offset-y: -(100 - $slide-offset),
    content-vertical-distance-property: top,
    arrow-x: -50%,
    arrow-y: 0,
    arrow-top: -$space-m,
    arrow-left: 50%,
    arrow-rotate: 0,
  ),
  center-bottom: (
    left: 50%,
    content-x: -50%,
    content-y: 100%,
    content-offset-x: -50%,
    content-offset-y: 100 - $slide-offset,
    content-vertical-distance-property: bottom,
    arrow-x: -50%,
    arrow-y: 0,
    arrow-bottom: -$space-m,
    arrow-left: 50%,
    arrow-rotate: 180deg,
  ),
  left-top: (
    left: -$space-m,
    content-x: 0,
    content-y: -100%,
    content-offset-x: 0,
    content-offset-y: -(100 - $slide-offset),
    content-vertical-distance-property: top,
    arrow-x: -50%,
    arrow-y: 0,
    arrow-top: -$space-m,
    arrow-left: 50%,
    arrow-rotate: 0,
  ),
  left-center: (
    top: 50%,
    content-x: -100%,
    content-y: -50%,
    content-offset-x: -(100 - ($slide-offset / 2)),
    content-offset-y: -50%,
    content-horizontal-distance-property: left,
    arrow-x: -25%,
    arrow-y: -50%,
    arrow-left: -$space-m,
    arrow-top: 50%,
    arrow-rotate: -90deg,
  ),
  left-bottom: (
    left: -$space-m,
    content-x: 0,
    content-y: 100%,
    content-offset-x: 0,
    content-offset-y: 100 - $slide-offset,
    content-vertical-distance-property: bottom,
    arrow-x: -50%,
    arrow-y: 0,
    arrow-left: 50%,
    arrow-bottom: -$space-m,
    arrow-rotate: 180deg,
  ),
  right-top: (
    right: -$space-m,
    content-x: 0,
    content-y: -100%,
    content-offset-x: 0,
    content-offset-y: -(100 - $slide-offset),
    content-vertical-distance-property: top,
    arrow-x: -50%,
    arrow-y: 0,
    arrow-left: 50%,
    arrow-top: -$space-m,
    arrow-rotate: 0,
  ),
  right-center: (
    top: 50%,
    content-x: 100%,
    content-y: -50%,
    content-offset-x: 100 - ($slide-offset / 2),
    content-offset-y: -50%,
    content-horizontal-distance-property: right,
    arrow-x: 25%,
    arrow-y: -50%,
    arrow-top: 50%,
    arrow-right: -$space-m,
    arrow-rotate: 90deg,
  ),
  right-bottom: (
    right: -$space-m,
    content-x: 0,
    content-y: 100%,
    content-offset-x: 0,
    content-offset-y: 100 - $slide-offset,
    content-vertical-distance-property: bottom,
    arrow-x: -50%,
    arrow-y: 0%,
    arrow-left: 50%,
    arrow-bottom: -$space-m,
    arrow-rotate: 180deg,
  )
);
$sizes: (
  m: (
    padding: $space-m,
    font-size: $font-m,
    arrow-container: $space-m,
    arrow-size: 12px,
  ),
  s: (
    padding: $space-1s $space-s,
    font-size: $font-s,
    arrow-container: $space-s,
    arrow-size: 6px,
  )
);

.tooltip {
  display: inline-flex;
  position: relative;
  cursor: pointer;

  &:focus {
    @extend %keyboard-nav-focus;
  }
}

.tooltip__toggle {
  display: inline-flex;
  position: relative;

  &:focus {
    @extend %keyboard-nav-focus;
  }
}

.tooltip__content,
.tooltip__arrow {
  &.fade-enter-active,
  &.fade-leave-active {
    transition: opacity $timing-1s $easing, transform $timing-1s $easing;
  }

  &.fade-enter,
  &.fade-leave-to {
    opacity: 0;
  }
}

.tooltip__content {
  @include elevation(2);

  position: absolute;
  width: max-content;
  max-width: $max-width;
  border-radius: $border-radius-l;
  color: var(--color-text-primary);
  background-color: var(--color-background-elevation-1);
  cursor: default;
  z-index: $z-index-1l;

  &:focus {
    @extend %keyboard-nav-focus;
  }
}

.tooltip__arrow {
  position: absolute;
  overflow: hidden;
  pointer-events: none;
  z-index: $z-index-2l;

  &::after {
    @include elevation(2);

    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
    background-color: var(--color-background-elevation-1);
  }
}

@each $size, $map in $sizes {
  $padding: map-get($map, padding);
  $font-size: map-get($map, font-size);
  $arrow-container: map-get($map, arrow-container);
  $arrow-size: map-get($map, arrow-size);

  .tooltip--size-#{$size} {
    .tooltip__content {
      font-size: $font-size;
    }

    .tooltip__arrow {
      width: $arrow-container * 2;
      height: $arrow-container;

      &::after {
        width: $arrow-size;
        height: $arrow-size;
      }
    }

    &.tooltip--padding {
      .tooltip__content {
        padding: $padding;
      }
    }
  }
}

@each $side, $map in $sides {
  $left: map-get($map, left);
  $right: map-get($map, right);
  $top: map-get($map, top);
  $content-x: map-get($map, content-x);
  $content-y: map-get($map, content-y);
  $content-offset-x: map-get($map, content-offset-x);
  $content-offset-y: map-get($map, content-offset-y);
  $content-vertical-distance-property: map-get($map, content-vertical-distance-property);
  $content-horizontal-distance-property: map-get($map, content-horizontal-distance-property);
  $arrow-x: map-get($map, arrow-x);
  $arrow-y: map-get($map, arrow-y);
  $arrow-left: map-get($map, arrow-left);
  $arrow-right: map-get($map, arrow-right);
  $arrow-top: map-get($map, arrow-top);
  $arrow-bottom: map-get($map, arrow-bottom);
  $arrow-rotate: map-get($map, arrow-rotate);

  .tooltip--side-#{$side} {
    .tooltip__content {
      transform: translate($content-x, $content-y);

      @if $left {
        left: $left;
      }

      @if $right {
        right: $right;
      }

      @if $top {
        top: $top;
      }

      @if $content-horizontal-distance-property {
        #{$content-horizontal-distance-property}: 50%;
      }

      &.fade-enter,
      &.fade-leave-to {
        transform: translate($content-offset-x, $content-offset-y);
      }
    }

    .tooltip__arrow {
      transform: translate($arrow-x, $arrow-y) rotate($arrow-rotate);

      @if $arrow-top {
        top: $arrow-top;
      }

      @if $arrow-left {
        left: $arrow-left;
      }

      @if $arrow-right {
        right: $arrow-right;
      }

      @if $arrow-bottom {
        bottom: $arrow-bottom;
      }

      @if str-index($side, 'bottom') or str-index($side, 'right-center') {
        &::after {
          @include elevation(2-inverted);
        }
      }
    }

    @each $size, $map in $sizes {
      $arrow-container: map-get($map, arrow-container);

      &.tooltip--size-#{$size} .tooltip__content {
        @if $content-vertical-distance-property {
          #{$content-vertical-distance-property}: -$arrow-container;
        }

        @if $content-horizontal-distance-property {
          #{$content-horizontal-distance-property}: -$arrow-container;
        }
      }
    }
  }
}
</style>
